import { useForm } from '@tanstack/react-form';
import { useAtom, useSetAtom } from 'jotai';
import { uniqueId } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import type { AreaSelection, DifferentialScenarioFormData } from './types';
import { saveScenarioLoadableAtom, toastAtom } from './atoms';
import {
  buildCreatePayload,
  buildUpdatePayload,
  DEFAULT_AREA_SELECTION,
  GLOBAL_SELECTION_ID,
  hasDuplicateProducts,
  isAreaSelectionComplete,
  isLineItemValid,
  type FormMode,
} from './utils';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';

// ─────────────────────────────────────
// Context type
// ─────────────────────────────────────

export interface ScenarioFormContextType {
  form: ReturnType<typeof useCreateForm>;
  mode: FormMode;
  selectedId: string | number;
  setSelectedId: (id: string | number) => void;
  addArea: () => void;
  removeArea: (id: number | string) => void;
  handleSave: () => void;
  isSaving: boolean;
}

const ScenarioFormContext = createContext<ScenarioFormContextType | null>(null);

// ─────────────────────────────────────
// Provider
// ─────────────────────────────────────

interface ScenarioFormProviderProps {
  initialValues: DifferentialScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  geometries: Record<string, GeoJSON.Geometry>;
  children: ReactNode;
}

const useCreateForm = (
  defaultValues: DifferentialScenarioFormData,
  geometries: Record<string, GeoJSON.Geometry>
) => {
  return useForm({
    defaultValues,
    validators: {
      onChange: ({ value }: { value: DifferentialScenarioFormData }) => {
        const hasIncompleteArea = value.areas.some(
          (a) => !isAreaSelectionComplete(a)
        );
        if (hasIncompleteArea) return 'All areas must have a selection';

        const ids = getMemoizedOverlappingAreaIds(value.areas, geometries);
        if (ids.size > 0)
          return 'One or more areas have overlapping geometries';

        for (const area of value.areas) {
          if (area.lineItems.some((li) => !isLineItemValid(li)))
            return 'One or more differentials are incomplete';

          if (hasDuplicateProducts(area.lineItems))
            return 'Only one differential per product per area';
        }

        return undefined;
      },
    },
  });
};

/**
 * Provides form state management for the differential scenario form.
 * @param {ScenarioFormProviderProps} props - The provider props
 * @return {JSX.Element} The provider component
 */
export function ScenarioFormProvider({
  initialValues,
  mode,
  scenarioId,
  geometries,
  children,
}: ScenarioFormProviderProps): JSX.Element {
  const form = useCreateForm(initialValues, geometries);
  const [selectedId, setSelectedId] = useState<string | number>(
    GLOBAL_SELECTION_ID
  );
  const [saveState, triggerSave] = useAtom(saveScenarioLoadableAtom);
  const removedAreaIdsRef = useRef<number[]>([]);
  const history = useHistory();

  const isSaving = saveState.state === 'loading';
  const setToast = useSetAtom(toastAtom);

  /**
   * Adds a new area with default values and selects it.
   */
  const addArea = useCallback(() => {
    const id = Number(uniqueId());
    const currentAreas = form.getFieldValue('areas');
    form.setFieldValue('areas', [
      ...currentAreas,
      { ...DEFAULT_AREA_SELECTION, lineItems: [], id },
    ]);
    setSelectedId(id);
  }, [form]);

  /**
   * Removes an area by ID. Tracks backend IDs for _destroy on update.
   * @param {number | string} id - The area ID to remove
   */
  const removeArea = useCallback(
    (id: number | string) => {
      const currentAreas = form.getFieldValue('areas');
      const area = currentAreas.find((a: AreaSelection) => a.id === id);
      if (
        area?.status === 'saved' &&
        !removedAreaIdsRef.current.includes(area.id)
      ) {
        removedAreaIdsRef.current.push(area.id);
      }
      form.setFieldValue(
        'areas',
        currentAreas.filter((a: AreaSelection) => a.id !== id)
      );

      if (selectedId === id) {
        setSelectedId(GLOBAL_SELECTION_ID);
      }
    },
    [form, selectedId]
  );

  /**
   * Saves the scenario to the backend (POST on create, PATCH on edit).
   */
  const handleSave = useCallback(async () => {
    const values = form.state.values;
    setToast(null);

    if (mode === 'create') {
      const payload = buildCreatePayload(values);
      try {
        const response = await triggerSave({ mode: 'create', payload });
        setToast({ message: 'Scenario saved', type: 'success' });
        history.replace(
          routes.differentialScenarioDetailPath.replace(
            ':id',
            String(response.id)
          )
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    } else {
      const payload = buildUpdatePayload(values, removedAreaIdsRef.current);
      try {
        await triggerSave({
          mode: 'edit',
          id: Number(scenarioId),
          payload,
        });
        setToast({ message: 'Scenario saved', type: 'success' });
        window.location.reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    }
  }, [form, mode, scenarioId, triggerSave, history, setToast]);

  const value = useMemo(
    () => ({
      form,
      mode,
      selectedId,
      setSelectedId,
      addArea,
      removeArea,
      handleSave,
      isSaving,
    }),
    [form, mode, selectedId, addArea, removeArea, handleSave, isSaving]
  );

  return (
    <ScenarioFormContext.Provider value={value}>
      {children}
    </ScenarioFormContext.Provider>
  );
}

// ─────────────────────────────────────
// Hook
// ─────────────────────────────────────

/**
 * Returns the scenario form context. Must be used within a ScenarioFormProvider.
 * @return {ScenarioFormContextType} The scenario form context
 * @throws {Error} If used outside of ScenarioFormProvider
 */
export function useScenarioForm(): ScenarioFormContextType {
  const context = useContext(ScenarioFormContext);
  if (context === null) {
    throw new Error(
      'useScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context;
}
